import { useEffect, useState } from "react";
import api, { fmtErr, inr, fmtDateTime } from "../../lib/api";
import { FullScreenLoader } from "../../context/AuthContext";
import { toast } from "sonner";

export default function FranchiseEarnings() {
  const [rows, setRows] = useState(null);
  const [type, setType] = useState("");

  useEffect(() => {
    setRows(null);
    api.get("/franchise/earnings").then(({ data }) => setRows(data)).catch((e) => { toast.error(fmtErr(e)); setRows([]); });
  }, []);

  if (!rows) return <FullScreenLoader label="Loading earnings" />;
  const filtered = type ? rows.filter((r) => r.commission_type === type) : rows;
  const credited = filtered.filter((r) => r.status === "credited").reduce((a, r) => a + r.commission_amount, 0);
  const reversed = filtered.filter((r) => r.status === "reversed").reduce((a, r) => a + r.commission_amount, 0);

  return (
    <div className="space-y-5" data-testid="franchise-earnings-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-shipany-navy">Earnings</h1>
          <p className="text-sm text-slate-500">Per-shipment commission report. Domestic = 10% of base rate · International = weight slab.</p>
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="earnings-type-filter"
          className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-shipany-navy focus:border-shipany-blue focus:outline-none">
          <option value="">All types</option><option value="domestic">Domestic</option><option value="international">International</option>
        </select>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="earnings-credited-card"><p className="text-xs font-semibold uppercase text-slate-400">Credited</p><p className="mono mt-1 text-xl font-bold text-emerald-600">{inr(credited)}</p></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="earnings-reversed-card"><p className="text-xs font-semibold uppercase text-slate-400">Reversed</p><p className="mono mt-1 text-xl font-bold text-red-500">{inr(reversed)}</p></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="earnings-count-card"><p className="text-xs font-semibold uppercase text-slate-400">Commissions</p><p className="mono mt-1 text-xl font-bold text-shipany-navy">{filtered.length}</p></div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[860px] text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs font-bold uppercase tracking-wide text-slate-400">
              <th className="px-5 py-3">Date</th><th className="px-5 py-3">Customer</th><th className="px-5 py-3">Type</th>
              <th className="px-5 py-3 text-right">Invoice</th><th className="px-5 py-3">Rule</th><th className="px-5 py-3 text-right">Commission</th><th className="px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.commission_id} className="border-b border-slate-50" data-testid={`earning-row-${r.commission_id}`}>
                <td className="px-5 py-3.5 text-xs text-slate-500">{fmtDateTime(r.created_at)}</td>
                <td className="px-5 py-3.5"><p className="text-xs font-semibold">{r.customer_name}</p><p className="text-[10px] text-slate-400">{r.customer_company}</p></td>
                <td className="px-5 py-3.5 text-xs capitalize">{r.commission_type}{r.weight ? ` · ${r.weight} kg` : ""}</td>
                <td className="mono px-5 py-3.5 text-right text-xs">{inr(r.invoice_amount || 0)}</td>
                <td className="px-5 py-3.5 text-[10px] text-slate-400">{r.commission_rule}</td>
                <td className="mono px-5 py-3.5 text-right text-xs font-bold text-emerald-600">{inr(r.commission_amount)}</td>
                <td className="px-5 py-3.5"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${r.status === "credited" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`} data-testid={`earning-status-${r.commission_id}`}>{r.status}</span></td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-400" data-testid="earnings-empty">No commissions yet — they appear when your customers book shipments.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
